import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import ObjectPath from 'object-path';
import cx from 'classnames';
import { H1 } from '@hobs/components/Headings';
import Toast from '@hobs/components/Toast';
import {
  Formik,
  Field,
  Form,
  FieldArray,
  yupToFormErrors,
  setNestedObjectValues
} from 'formik';
import { differenceInCalendarDays, differenceInCalendarMonths } from 'date-fns';
import { GhostButton, RaisedButton } from '@hobs/components/Buttons';
import Icon from '@hobs/components/Icon';
import Confirm from '@hobs/components/Confirm';
import Dialog from '@hobs/components/Dialog';
import Alert from '@hobs/components/Alert';
import Tooltip from '@hobs/components/Tooltip';
import NumericInput from '@hobs/components/NumericInput';
import Autocomplete from '@hobs/components/Autocomplete';
import { Download } from '@hobs/utils/download';
import Dropdown from '@hobs/components/Dropdown';
import DateInput from '@hobs/components/DateInput';
import PageActions from '@hobs/components/PageActions';
import {
  BACKEND_DATE_FORMAT,
  MEAL_PLANS_EXPORT_FILE_NAME,
  USER_ROLES,
  MONTH_NAMES
} from '@hobs/utils/constants';
import { format } from '@hobs/utils/formatWithTimeZone';
import { dateToInitialMonth } from '@hobs/utils/dateToInitialMonth';
import * as Yup from 'yup';

import './MealPlanner.scss';

const MealPlanItemFields = ({
  mealPlanItems,
  todayDate,
  setFieldValue,
  serviceRequestIndex,
  groupIndex,
  canEditMealPlanner
}) => {
  if (!mealPlanItems.items) {
    return null;
  }

  return mealPlanItems.items.map((mealPlan, index, array) => {
    const keyName = `serviceRequests.${serviceRequestIndex}.mealPlanItems.${groupIndex}.items.${index}.quantity`;

    const month = new Date(mealPlan.date).getMonth();
    const lastMealPlanMonth =
      index !== 0 ? new Date(array[index - 1].date).getMonth() : null;
    const nextMealPlanMonth =
      array.length !== index + 1
        ? new Date(array[index + 1].date).getMonth()
        : null;

    return (
      <React.Fragment key={keyName}>
        <td
          className={cx('day', {
            disabled: new Date(mealPlan.date) < todayDate,
            'first-day-of-month':
              index === 0 || (lastMealPlanMonth && lastMealPlanMonth !== month),
            'last-day-of-month':
              (nextMealPlanMonth !== null && month !== nextMealPlanMonth) ||
              index === array.length - 1
          })}
        >
          <Field
            name={keyName}
            setFieldValue={setFieldValue}
            disabled={!mealPlan.isEnabled || canEditMealPlanner}
            showButtons={false}
            min={0}
            valueNullable={true}
            noMargin
            component={NumericInput}
          />
        </td>
        {nextMealPlanMonth !== null && month !== nextMealPlanMonth && (
          <td className="spacer">
            <span />
          </td>
        )}
      </React.Fragment>
    );
  });
};

const MemoizedMealPlanItemFields = React.memo(MealPlanItemFields);

class MealPlanner extends PureComponent {
  constructor(props) {
    super(props);
    const { t } = props;

    this.serviceRequestInfoVerticalScroll = React.createRef();
    this.serviceRequestDataVerticalScroll = React.createRef();

    const filterDateFrom = new Date();
    const filterDateTo = new Date();
    filterDateTo.setDate(filterDateFrom.getDate() + 13);

    this.state = {
      todayDate: new Date(),
      filter: {
        dateFrom: filterDateFrom,
        dateTo: filterDateTo,
        locationId: null,
        nationId: null,
        kitchenId: null,
        unitId: null
      },
      kitchenDialog: {
        isOpen: false
      },
      hoveredTable: null
    };

    this.container = React.createRef();
    this.formik = React.createRef();

    const mpValidation = Yup.object().shape({
      quantity: Yup.number().when('isEnabled', {
        is: true,
        then: Yup.number()
          .nullable()
          .typeError('Invalid number')
          .min(0, 'Invalid number')
          .required(t('Enter q-ty')),
        otherwise: Yup.number().nullable()
      })
    });

    this.validationSchema = Yup.object().shape({
      serviceRequests: Yup.array().of(
        Yup.object().shape({
          mealPlans: Yup.array().when('mealPlanItems', {
            is: mpi => mpi.length === 0,
            then: Yup.array().of(mpValidation),
            otherwise: Yup.array()
          }),
          mealPlanItems: Yup.array().of(
            Yup.object().shape({
              items: Yup.array().of(
                Yup.object().shape({
                  quantity: Yup.number().when('isEnabled', {
                    is: true,
                    then: Yup.number()
                      .nullable()
                      .typeError('Invalid number')
                      .min(0, 'Invalid number')
                      .required(t('Enter q-ty')),
                    otherwise: Yup.number().nullable()
                  })
                })
              ),
              mealPlanSubServiceId: Yup.string()
                .typeError(t('Service name is required'))
                .required(t('Service name is required'))
            })
          )
        })
      )
    });

    this.filterSchema = Yup.object().shape({
      dateFrom: Yup.date()
        .typeError(t('Incorrect date or date format'))
        .required(t('Date from is required'))
        .nullable(),
      dateTo: Yup.date()
        .typeError(t('Incorrect date or date format'))
        .required(t('Date to is required'))
        .min(Yup.ref('dateFrom'), 'Date to cannot be earlier than Date from')
        .nullable()
    });
  }

  componentDidMount() {
    this.loadRequiredData();
  }

  loadRequiredData() {
    const { filter } = this.state;
    this.props
      .getServiceRequests(
        format(filter.dateFrom, BACKEND_DATE_FORMAT),
        format(filter.dateTo, BACKEND_DATE_FORMAT)
      )
      .then(() => {
        if (this.formik && this.formik.current) {
          const bag = this.formik.current.getFormikBag();
          bag.setSubmitting(false);
          this.validateData(bag.values, bag.setErrors, bag.setTouched);
        }
      });
    this.props.getMealPlannerForSubmit();
  }

  getInitialValues = () => {
    const { serviceRequests } = this.props;

    const initialValues = {
      serviceRequests: serviceRequests.map(item => {
        const emptyArray = [...item.mealPlans].map(mp => {
          return {
            date: mp.date,
            isEnabled: mp.isEnabled,
            mealPlanId: null,
            quantity: null,
            orderNumber: null
          };
        });

        const mealPlanItems = item.mealPlans.reduce((acc, mp) => {
          const groups = mp.mealPlanItems.reduce((mpItemsAcc, mpItem) => {
            if (!mpItemsAcc[mpItem.orderNumber]) {
              mpItemsAcc[mpItem.orderNumber] = {
                items: [],
                mealPlanSubServiceId: mpItem.mealPlanSubServiceId,
                mealPlanSubServiceName: mpItem.mealPlanSubServiceName
              };
            }

            if (
              !mpItemsAcc[mpItem.orderNumber].mealPlanSubServiceId &&
              mpItem.mealPlanSubServiceId
            ) {
              mpItemsAcc[mpItem.orderNumber].mealPlanSubServiceId =
                mpItem.mealPlanSubServiceId;
              mpItemsAcc[mpItem.orderNumber].mealPlanSubServiceName =
                mpItem.mealPlanSubServiceName;
            }

            mpItemsAcc[mpItem.orderNumber].items.push({
              ...mpItem,
              date: mp.date
            });

            return mpItemsAcc;
          }, acc);
          return groups;
        }, []);

        const correctMealPlanItems = mealPlanItems
          .map(group => {
            const items = [...emptyArray].map(i => {
              const gi = group.items.find(gi => gi.date === i.date);

              if (gi) {
                return gi;
              }

              return i;
            });

            return {
              ...group,
              items
            };
          })
          .filter(x => Object.keys(x).length > 0);
        return {
          ...item,
          mealPlanItems: correctMealPlanItems
        };
      })
    };

    return initialValues;
  };

  reloadServiceRequests(callback) {
    const { filter } = this.state;

    this.filterServiceRequests(filter, callback);
  }

  filterServiceRequests = (values, callback) => {
    const filter = {
      dateFrom: format(values.dateFrom, BACKEND_DATE_FORMAT),
      dateTo: format(values.dateTo, BACKEND_DATE_FORMAT),
      locationName: values.locationName ? values.locationName : '',
      kitchenId: values.kitchenId ? values.kitchenId : '',
      nationId: values.nationId ? values.nationId : '',
      unitId: values.unitId ? values.unitId : ''
    };

    this.props
      .getServiceRequests(
        filter.dateFrom,
        filter.dateTo,
        filter.locationName,
        filter.kitchenId,
        filter.nationId,
        filter.unitId
      )
      .then(() => {
        if (this.formik && this.formik.current) {
          const bag = this.formik.current.getFormikBag();
          this.validateData(bag.values, bag.setErrors, bag.setTouched);
        }

        if (callback && typeof callback === 'function') {
          callback();
        }
      });

    this.setState({
      filter: {
        ...filter,
        dateFrom: values.dateFrom,
        dateTo: values.dateTo
      }
    });
  };

  validateData(values, setErrors, setTouched) {
    this.validationSchema
      .validate(values, { abortEarly: false })
      .catch(errors => {
        setErrors(yupToFormErrors(errors));
        setTouched(setNestedObjectValues(values, true));
      });
  }

  onLocationQueryChange = query => {
    this.props.queryLocationsForMealPlanner(query);
  };

  toAutocompleteEntity(data) {
    return data
      ? data.map(item => ({
          value: item,
          title: item
        }))
      : [];
  }

  exportMealPlans = async () => {
    const { filter } = this.state;

    const { serviceRequests, exportMealPlans } = this.props;
    const body = {
      serviceRequests,
      dateFrom: format(filter.dateFrom, BACKEND_DATE_FORMAT),
      dateTo: format(filter.dateTo, BACKEND_DATE_FORMAT)
    };
    const data = await exportMealPlans(body);
    Download(
      data.blob,
      `${MEAL_PLANS_EXPORT_FILE_NAME}_${format(
        new Date(),
        'yyyyMMdd_HHmm'
      )}.xlsx`
    );
  };

  renderHeader() {
    const { t, serviceRequests } = this.props;
    return (
      <div className="meal-planner--header">
        <H1>{t('Meal planner')}</H1>
        <GhostButton
          iconLeft={<Icon icon="download" />}
          onClick={this.exportMealPlans}
          disabled={!serviceRequests.length}
        >
          {t('XLSX')}
        </GhostButton>
      </div>
    );
  }

  renderNationFilter(setFieldValue) {
    const { forSubmit, t } = this.props;

    return (
      <div className="filter">
        <Field
          name="nationId"
          setFieldValue={setFieldValue}
          label={t('Nation') + ':'}
          wrapperClass="dropdown-filter-input"
          component={Dropdown}
          onChange={event => {
            const nationId = event.target.value;

            if (nationId !== null && nationId !== '') {
              setFieldValue('unitId', null);
            }
          }}
        >
          {forSubmit.sendingNations.length ? (
            <>
              <option value="">{t('All nations')}</option>
              {forSubmit.sendingNations.map(nation => {
                return (
                  <option key={nation.id} value={nation.id}>
                    {nation.title}
                  </option>
                );
              })}
            </>
          ) : (
            <option value="">{t('There are no SNs for this Exercise')}</option>
          )}
        </Field>
      </div>
    );
  }

  renderFilters() {
    const { t, forSubmit } = this.props;
    const { filter } = this.state;
    return (
      <Formik
        validateOnBlur={false}
        validateOnChange={false}
        enableReinitialize={true}
        initialValues={filter}
        validate={() => {
          return true;
        }}
        validationSchema={this.filterSchema}
        onSubmit={this.filterServiceRequests}
      >
        {({ setFieldValue, values }) => (
          <Form className="meal-planner--filters">
            <div className="filter">
              <Field
                label={t('Location')}
                placeholder={t('Location')}
                name="locationId"
                items={this.toAutocompleteEntity(
                  this.props.mealPlannerLocations
                )}
                inputClass="location"
                onItemSelect={item => {
                  setFieldValue(
                    'locationName',
                    ObjectPath.get(item, 'value', null)
                  );
                }}
                noResultsMessage={t('Location not found')}
                onQueryChange={this.onLocationQueryChange}
                component={Autocomplete}
                defaultQuery={values.locationName}
              >
                {t('Location')}
              </Field>
            </div>
            <div className="filter">
              <Field
                name="kitchenId"
                setFieldValue={setFieldValue}
                label={t('Kitchen') + ':'}
                wrapperClass="dropdown-filter-input"
                component={Dropdown}
              >
                {forSubmit.kitchens.length ? (
                  <>
                    <option value="">{t('All kitchens')}</option>
                    {forSubmit.kitchens.reduce((result, kitchen) => {
                      if (
                        values.locationId === null ||
                        values.locationId === '' ||
                        values.locationId === undefined ||
                        kitchen.locationId === values.locationId
                      ) {
                        result.push(
                          <option
                            key={kitchen.kitchenId}
                            value={kitchen.kitchenId}
                          >
                            {kitchen.kitchenName}
                          </option>
                        );
                      }

                      return result;
                    }, [])}
                  </>
                ) : (
                  <option value="">
                    {t('There are no Kitchens for this Exercise')}
                  </option>
                )}
              </Field>
            </div>
            {this.renderNationFilter(setFieldValue)}
            <div className="filter">
              <Field
                name="unitId"
                setFieldValue={setFieldValue}
                label={t('Unit') + ':'}
                wrapperClass="dropdown-filter-input"
                component={Dropdown}
              >
                {forSubmit.units.length ? (
                  <>
                    <option value="">{t('All units')}</option>
                    {forSubmit.units.reduce((result, unit) => {
                      if (
                        values.nationId === null ||
                        values.nationId === '' ||
                        values.nationId === unit.partyId
                      ) {
                        result.push(
                          <option key={unit.unitId} value={unit.unitId}>
                            {unit.unitName}
                          </option>
                        );
                      }

                      return result;
                    }, [])}
                  </>
                ) : (
                  <option value="">
                    {t('There are no units for this Exercise')}
                  </option>
                )}
              </Field>
            </div>
            <div className="filter small">
              <Field
                name="dateFrom"
                setFieldValue={setFieldValue}
                noMargin
                wrapperClass="date-filter-input"
                component={DateInput}
              >
                {t('From')}:
              </Field>
            </div>
            <div className="filter small">
              <Field
                name="dateTo"
                setFieldValue={setFieldValue}
                noMargin
                initialMonth={dateToInitialMonth(
                  values.dateFrom,
                  values.dateTo
                )}
                wrapperClass="date-filter-input"
                component={DateInput}
              >
                {t('To')}:
              </Field>
            </div>
            <div className="filter filter-button-wrapper">
              <RaisedButton className="filter-button">
                {t('Filter')}
              </RaisedButton>
            </div>
          </Form>
        )}
      </Formik>
    );
  }

  getMonthColumnHeaders() {
    const { serviceRequests } = this.props;

    if (serviceRequests.length) {
      const firstServiceRequest = serviceRequests[0];

      if (firstServiceRequest.mealPlans) {
        const months = firstServiceRequest.mealPlans.reduce(
          (result, mealPlan, _, array) => {
            const month = new Date(mealPlan.date).getMonth();

            if (!result.find(item => item.month === month)) {
              const date = format(mealPlan.date, 'yyyy');

              const timeRepeat = array.filter(
                item => new Date(item.date).getMonth() === month
              );

              result.push({
                month: month,
                date: `${MONTH_NAMES[month]}, ${date}`,
                timeRepeat: timeRepeat.length
              });
            }

            return result;
          },
          []
        );

        return months.map((mealPlan, index, array) => {
          return (
            <React.Fragment key={`header.${index}.month`}>
              <th className="month" colSpan={mealPlan.timeRepeat}>
                {mealPlan.date}
              </th>
              {array.length !== 1 && (
                <th className="spacer">
                  <span />
                </th>
              )}
            </React.Fragment>
          );
        });
      }
    }
  }

  getDateColumnHeaders() {
    const { serviceRequests } = this.props;
    const { todayDate } = this.state;

    if (serviceRequests.length) {
      const firstServiceRequest = serviceRequests[0];

      if (firstServiceRequest.mealPlans) {
        return firstServiceRequest.mealPlans.map((mealPlan, index, array) => {
          const month = new Date(mealPlan.date).getMonth();
          const lastMealPlanMonth =
            index !== 0 ? new Date(array[index - 1].date).getMonth() : null;
          const nextMealPlanMonth =
            array.length !== index + 1
              ? new Date(array[index + 1].date).getMonth()
              : null;
          return (
            <React.Fragment
              key={`header.${index}.day.${firstServiceRequest.date}`}
            >
              <th
                className={cx('day', {
                  disabled: new Date(mealPlan.date) < todayDate,
                  'first-day-of-month':
                    index === 0 ||
                    (lastMealPlanMonth && lastMealPlanMonth !== month),
                  'last-day-of-month':
                    (nextMealPlanMonth !== null &&
                      month !== nextMealPlanMonth) ||
                    index === array.length - 1
                })}
              >
                {format(mealPlan.date, 'dd')}
              </th>
              {nextMealPlanMonth !== null && month !== nextMealPlanMonth && (
                <th className="spacer">
                  <span />
                </th>
              )}
            </React.Fragment>
          );
        });
      }
    }
  }

  getKitchenName(serviceRequest) {
    const { t } = this.props;

    return serviceRequest.kitchenName ? (
      <Tooltip tooltip={serviceRequest.kitchenName} position="top">
        {serviceRequest.kitchenName}
      </Tooltip>
    ) : (
      <span>{t('Unspecified')}</span>
    );
  }

  handleSplit = (serviceRequest, arrayHelpers) => () => {
    const items = new Array(
      serviceRequest.mealPlanItems.length === 0 ? 3 : 1
    ).fill({
      mealPlanSubServiceId: null,
      items: serviceRequest.mealPlans.map(item => {
        return {
          date: item.date,
          isEnabled: item.isEnabled,
          mealPlanId: null,
          quantity: item.isEnabled && item.quantity ? item.quantity : null,
          serviceRequestId: null,
          orderNumber: 0
        };
      })
    });

    items.forEach(item => {
      arrayHelpers.push(item);
    });
  };

  renderKitchenName(serviceRequest, serviceRequestIndex) {
    const { t } = this.props;
    return (
      <>
        {this.canEditMealPlanner() ? (
          <button
            className="kitchen-assign-button"
            onClick={event => {
              event.preventDefault();
              if (serviceRequest.locationName) {
                this.setState({
                  kitchenDialog: {
                    ...this.state.kitchenDialog,
                    selectedKitchenId: serviceRequest.kitchenId,
                    serviceRequestIndex: serviceRequestIndex,
                    serviceRequestId: serviceRequest.serviceRequestId,
                    locationName: serviceRequest.locationName,
                    isOpen: true
                  }
                });
              } else {
                Toast.warning(
                  t('Location is not selected. Please select it first')
                );
              }
            }}
          >
            {this.getKitchenName(serviceRequest)}
          </button>
        ) : (
          this.getKitchenName(serviceRequest)
        )}
      </>
    );
  }

  renderServiceRequestMealPlanItems = (
    serviceRequest,
    serviceRequestIndex,
    setFieldValue
  ) => {
    const { filter, todayDate } = this.state;

    const to = new Date(filter.dateTo);
    const from = new Date(filter.dateFrom);

    const count = differenceInCalendarDays(to, from);

    const months = differenceInCalendarMonths(to, from);

    const lines = serviceRequest.mealPlanItems.map(
      (mealPlanItems, groupIndex) => {
        return (
          <tr
            key={`${serviceRequest.serviceRequestId}.serviceRequests.${serviceRequestIndex}.mealPlanItems.${groupIndex}`}
          >
            <FieldArray
              name={`serviceRequests.${serviceRequestIndex}.mealPlanItems.${groupIndex}`}
              render={() => {
                return (
                  <MemoizedMealPlanItemFields
                    mealPlanItems={mealPlanItems}
                    todayDate={todayDate}
                    setFieldValue={setFieldValue}
                    serviceRequestIndex={serviceRequestIndex}
                    groupIndex={groupIndex}
                    canEditMealPlanner={!this.canEditMealPlanner()}
                  />
                );
              }}
            />
          </tr>
        );
      }
    );

    return (
      <>
        {lines}
        {serviceRequest.mealPlanItems.length > 0 &&
          this.canEditMealPlanner() &&
          this.canSplitMealPlanner() && (
            <tr>
              <td colSpan={count + months + 1}></td>
            </tr>
          )}
      </>
    );
  };

  renderServiceRequestMealPlanItemsSelects = (serviceRequest, index) => {
    const { t, forSubmit } = this.props;

    if (
      serviceRequest.mealPlanItems &&
      serviceRequest.mealPlanItems.length === 0
    ) {
      return null;
    }

    return (
      <>
        <tr>
          <td className="no-padd meal-planner--split-select" colSpan={7}>
            <FieldArray
              name={`serviceRequests.${index}.mealPlanItems`}
              render={arrayHelpers => {
                return serviceRequest.mealPlanItems.map(
                  (mpItem, groupIndex) => {
                    const isDisabled =
                      !this.canSplitMealPlanner() ||
                      (serviceRequest.hasSplittedMealItems &&
                        !serviceRequest.hasEnabledMealPlans) ||
                      (serviceRequest.hasSplittedMealItems &&
                        mpItem.mealPlanSubServiceName);

                    return (
                      <div
                        key={`mealPlanItemSelect.${serviceRequest.serviceRequestId}.${groupIndex}`}
                      >
                        <Field
                          name={`serviceRequests.${index}.mealPlanItems.${groupIndex}.mealPlanSubServiceId`}
                          disabled={isDisabled}
                          component={Dropdown}
                        >
                          {!forSubmit.mealPlanSubServices && (
                            <option value={mpItem.mealPlanSubServiceId}>
                              {mpItem.mealPlanSubServiceName}
                            </option>
                          )}
                          {forSubmit.mealPlanSubServices && (
                            <option value="">
                              {t('Select meal plan service')}
                            </option>
                          )}

                          {forSubmit.mealPlanSubServices &&
                            forSubmit.mealPlanSubServices.map(item => {
                              return (
                                <option
                                  key={item.subServiceId}
                                  value={item.subServiceId}
                                >
                                  {item.subServiceName}
                                </option>
                              );
                            })}

                          {(!forSubmit.mealPlanSubServices ||
                            !forSubmit.mealPlanSubServices.length === 0) && (
                            <option value="">
                              {t('There are no services available')}
                            </option>
                          )}
                        </Field>

                        {this.canSplitMealPlanner() &&
                          this.canEditMealPlanner() && (
                            <button
                              type="button"
                              onClick={() => {
                                arrayHelpers.remove(groupIndex);
                              }}
                              className="meal-planner--split-remove"
                            >
                              <Icon icon="trashBin" />
                            </button>
                          )}
                      </div>
                    );
                  }
                );
              }}
            />
          </td>
        </tr>
        {this.canEditMealPlanner() && this.canSplitMealPlanner() && (
          <tr>
            <td colSpan={7}>
              <FieldArray
                name={`serviceRequests.${index}.mealPlanItems`}
                render={arrayHelpers => {
                  return (
                    <GhostButton
                      className="meal-planner--add-more"
                      iconLeft={<Icon icon="plus" />}
                      size="small"
                      type="button"
                      disabled={!serviceRequest.hasEnabledMealPlans}
                      onClick={this.handleSplit(serviceRequest, arrayHelpers)}
                    >
                      <span className="meal-planner--add-more-text">
                        {t('Add more meals')}
                      </span>
                    </GhostButton>
                  );
                }}
              />
            </td>
          </tr>
        )}
      </>
    );
  };

  renderServiceRequestInfo(serviceRequest, serviceRequestIndex) {
    return (
      <React.Fragment
        key={`info.${serviceRequestIndex}.${serviceRequest.kitchenId}`}
      >
        <tr>
          <td className="request">
            {serviceRequest.requestNumber && (
              <Tooltip tooltip={serviceRequest.requestNumber} position="top">
                {serviceRequest.requestNumber}
              </Tooltip>
            )}
          </td>
          <td className="service">
            {serviceRequest.serviceName && (
              <Tooltip tooltip={serviceRequest.serviceName} position="top">
                {serviceRequest.serviceName}
              </Tooltip>
            )}
          </td>
          <td className="location">
            {serviceRequest.locationName && (
              <Tooltip tooltip={serviceRequest.locationName} position="top">
                {serviceRequest.locationName}
              </Tooltip>
            )}
          </td>
          <td className="kitchen">
            {this.renderKitchenName(serviceRequest, serviceRequestIndex)}
          </td>
          {this.canRenderNation() && (
            <td className="nation">
              {serviceRequest.sendingNationName && (
                <Tooltip
                  tooltip={serviceRequest.sendingNationName}
                  position="top"
                >
                  {serviceRequest.sendingNationName}
                </Tooltip>
              )}
            </td>
          )}
          <td className="unit">
            {serviceRequest.unitName && (
              <Tooltip tooltip={serviceRequest.unitName} position="top">
                {serviceRequest.unitName}
              </Tooltip>
            )}
          </td>
          {this.canSplitMealPlanner() && (
            <td className="split" align="center">
              {this.canEditMealPlanner() &&
                serviceRequest.mealPlanItems.length === 0 && (
                  <FieldArray
                    name={`serviceRequests.${serviceRequestIndex}.mealPlanItems`}
                    render={arrayHelpers => {
                      return (
                        <button
                          className="meal-planner--split-button"
                          type="button"
                          disabled={!serviceRequest.hasEnabledMealPlans}
                          onClick={this.handleSplit(
                            serviceRequest,
                            arrayHelpers
                          )}
                        >
                          <Icon icon="split" />
                        </button>
                      );
                    }}
                  />
                )}
            </td>
          )}
        </tr>

        {this.renderServiceRequestMealPlanItemsSelects(
          serviceRequest,
          serviceRequestIndex
        )}
      </React.Fragment>
    );
  }

  renderServiceRequest(serviceRequest, serviceRequestIndex, setFieldValue) {
    const { todayDate } = this.state;
    return (
      <React.Fragment
        key={`data.${serviceRequestIndex}.${serviceRequest.serviceRequestId}`}
      >
        <tr>
          <FieldArray
            name="mealPlans"
            render={() => {
              return serviceRequest.mealPlans.map((mealPlan, index, array) => {
                const month = new Date(mealPlan.date).getMonth();
                const lastMealPlanMonth =
                  index !== 0
                    ? new Date(array[index - 1].date).getMonth()
                    : null;
                const nextMealPlanMonth =
                  array.length !== index + 1
                    ? new Date(array[index + 1].date).getMonth()
                    : null;

                return (
                  <React.Fragment
                    key={`${serviceRequestIndex}${mealPlan.mealPlanId}.mealPlans.${index}.column.${serviceRequest.date}`}
                  >
                    <td
                      className={cx('day', {
                        disabled: new Date(mealPlan.date) < todayDate,
                        'first-day-of-month':
                          index === 0 ||
                          (lastMealPlanMonth && lastMealPlanMonth !== month),
                        'last-day-of-month':
                          (nextMealPlanMonth !== null &&
                            month !== nextMealPlanMonth) ||
                          index === array.length - 1
                      })}
                    >
                      <Field
                        name={`serviceRequests.${serviceRequestIndex}.mealPlans.${index}.quantity`}
                        setFieldValue={setFieldValue}
                        strike={
                          (serviceRequest.mealPlanItems.length > 0 &&
                            mealPlan.isEnabled) ||
                          (mealPlan.mealPlanItems.length > 0 &&
                            !mealPlan.isEnabled)
                        }
                        disabled={
                          !mealPlan.isEnabled ||
                          !this.canEditMealPlanner() ||
                          serviceRequest.mealPlanItems.length > 0
                        }
                        showButtons={false}
                        min={0}
                        valueNullable={true}
                        noMargin
                        component={NumericInput}
                      />
                    </td>
                    {nextMealPlanMonth !== null && month !== nextMealPlanMonth && (
                      <td className="spacer">
                        <span />
                      </td>
                    )}
                  </React.Fragment>
                );
              });
            }}
          />
        </tr>

        {this.renderServiceRequestMealPlanItems(
          serviceRequest,
          serviceRequestIndex,
          setFieldValue
        )}
      </React.Fragment>
    );
  }

  submit = (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    const { t, forSubmit } = this.props;
    const { filter } = this.state;

    const mealPlanItemsData = values.serviceRequests.map(serviceRequest => {
      const mealPlanItems = [...serviceRequest.mealPlanItems];

      const data = serviceRequest.mealPlans.map(item => {
        let highestOrderNumber = item.mealPlanItems.reduce(
          (accumulated, current) => {
            return Math.max(accumulated, current.orderNumber);
          },
          0
        );
        // Return only non empty mealPlanItems
        const mpItemsData = mealPlanItems.reduce((acc, group, groupIndex) => {
          const validItems = group.items.reduce((acc, groupItem) => {
            if (groupItem.quantity !== null && groupItem.date === item.date) {
              if (!groupItem.orderNumber) {
                const groupOrderNumber = group.items.find(
                  x => x.orderNumber !== null
                );

                if (groupOrderNumber && groupOrderNumber.orderNumber) {
                  groupIndex = groupOrderNumber.orderNumber;
                } else if (groupIndex <= highestOrderNumber) {
                  highestOrderNumber++;
                  groupIndex = highestOrderNumber;
                }
              }
              const mealPlanItem = {
                mealPlanSubServiceId: group.mealPlanSubServiceId,
                mealPlanSubServiceName: group.mealPlanSubServiceName,
                quantity: parseInt(groupItem.quantity, 10),
                orderNumber: groupItem.orderNumber
                  ? groupItem.orderNumber
                  : groupIndex
              };

              if (groupItem.mealPlanItemId) {
                mealPlanItem.mealPlanItemId = groupItem.mealPlanItemId;
              }

              // Only for HN, set Subservice name
              if (
                !group.mealPlanSubServiceName &&
                forSubmit.mealPlanSubServices
              ) {
                const mpSubServiceItem = forSubmit.mealPlanSubServices.find(
                  i => i.subServiceId === group.mealPlanSubServiceId
                );

                if (mpSubServiceItem) {
                  mealPlanItem.mealPlanSubServiceName =
                    mpSubServiceItem.subServiceName;
                }
              }

              acc.push(mealPlanItem);
            }

            return acc;
          }, []);

          if (validItems.length > 0) {
            acc.push(...validItems);
          }

          return acc;
        }, []);
        return {
          ...item,
          quantity: item.quantity ? parseInt(item.quantity, 10) : null,
          mealPlanItems: mpItemsData
        };
      });

      const originalData = { ...serviceRequest };

      delete originalData.mealPlanItems;

      return {
        ...originalData,
        mealPlans: data
      };
    });

    const body = {
      dateFrom: format(filter.dateFrom, BACKEND_DATE_FORMAT),
      dateTo: format(filter.dateTo, BACKEND_DATE_FORMAT),
      serviceRequests: mealPlanItemsData
    };

    this.props.updateServiceRequests(body).then(response => {
      if (response.success) {
        Toast.success(t('Meal plan updated successfully'));
        this.reloadServiceRequests(() => {
          resetForm();
        });
      } else if (response.payload) {
        let errors = '';
        if (response.playload.details) {
          response.payload.details.forEach(error => {
            errors = errors + ' ' + error.message[0];
          });
        }

        Toast.warning(response.payload.message + `${errors}`);

        resetForm();
      }
      setSubmitting(false);
    });
  };

  cancelChanges(resetForm) {
    const { t } = this.props;
    Confirm(t('Cancel'), t('Are you sure you want to cancel?'), () => {
      resetForm();
    });
  }

  onDialogOpening = () => {
    const { kitchenDialog } = this.state;
    const { getServiceRequestKitchenForSubmit } = this.props;

    getServiceRequestKitchenForSubmit(kitchenDialog.locationName);
  };

  updateKitchen() {
    const { updateServiceRequestKitchen, t } = this.props;
    const { kitchenDialog } = this.state;

    updateServiceRequestKitchen(
      kitchenDialog.serviceRequestId,
      kitchenDialog.selectedKitchenId
    ).then(response => {
      if (response.success) {
        Toast.success(t('Kitchen updated successfully'));

        this.reloadServiceRequests();
      } else if (response.payload) {
        Toast.warning(response.payload.message);
      }
    });

    this.setState({ kitchenDialog: {} });
  }

  renderKitchenDialog() {
    const { t, forSubmit, kitchenForSubmit } = this.props;
    const { kitchenDialog } = this.state;
    return (
      <Dialog
        title={t('Kitchen')}
        onOpening={this.onDialogOpening}
        isOpen={kitchenDialog.isOpen}
        onClose={() => {
          this.setState({ kitchenDialog: {} });
        }}
        className="kitchen-dialog"
      >
        {kitchenDialog.error && (
          <Alert type="error">{kitchenDialog.error}</Alert>
        )}
        <Dropdown
          label={t('Kitchen')}
          onChange={event => {
            const kitchenId = event.target.value;
            const kitchen = forSubmit.kitchens.find(
              kitchen => kitchen.kitchenId === kitchenId
            );
            this.setState({
              kitchenDialog: {
                ...this.state.kitchenDialog,
                selectedKitchenId: kitchenId,
                kitchenName: kitchen ? kitchen.kitchenName : null
              }
            });
          }}
        >
          {kitchenForSubmit.length ? (
            <>
              <option value="">{t('Select kitchen')}</option>
              {kitchenForSubmit &&
                kitchenForSubmit.map(kitchen => {
                  return (
                    <option key={kitchen.id} value={kitchen.id}>
                      {kitchen.title}
                    </option>
                  );
                })}
            </>
          ) : (
            <option value="">
              {t('There are no Kitchens for this Location')}
            </option>
          )}
        </Dropdown>
        <div className="kitchen-dialog--button">
          <RaisedButton
            onClick={() => {
              const { kitchenDialog } = this.state;
              if (kitchenDialog.selectedKitchenId) {
                this.updateKitchen();
              } else {
                this.setState({
                  kitchenDialog: {
                    ...kitchenDialog,
                    error: t('Kitchen is required')
                  }
                });
              }
            }}
          >
            {t('Save')}
          </RaisedButton>
        </div>
      </Dialog>
    );
  }

  canRenderNation() {
    return true;
  }

  canSplitMealPlanner() {
    return true;
  }

  canEditMealPlanner() {
    const { hasUserPermission } = this.props;
    return (
      !hasUserPermission(USER_ROLES.READONLY) &&
      !hasUserPermission(USER_ROLES.HOST_UNIT)
    );
  }

  synchronizeScroll = e => {
    if (this.state.hoveredTable === 'service-request-info') {
      this.serviceRequestDataVerticalScroll.current.scrollTop =
        e.target.scrollTop;
      return;
    }
    this.serviceRequestInfoVerticalScroll.current.scrollTop =
      e.target.scrollTop;
  };

  onTableMouseEnter = e => {
    this.setState({ hoveredTable: e.currentTarget.id });
  };

  renderServiceRequests() {
    const { t, serviceRequests } = this.props;

    return (
      <Formik
        ref={this.formik}
        validateOnBlur={true}
        validateOnChange={false}
        enableReinitialize={true}
        validationSchema={this.validationSchema}
        initialValues={this.getInitialValues()}
        onSubmit={this.submit}
      >
        {({ setFieldValue, values, isSubmitting, resetForm, dirty }) => (
          <Form noValidate={true} className="meal-planner--service-requests">
            {this.canEditMealPlanner() && (
              <PageActions
                container={this.container.current}
                ignoreBottomPadding
              >
                <div className="form-actions">
                  <GhostButton
                    large
                    disabled={isSubmitting || !dirty}
                    onClick={() => {
                      this.cancelChanges(resetForm);
                    }}
                  >
                    {t('Cancel')}
                  </GhostButton>
                  <RaisedButton
                    large
                    type="submit"
                    disabled={isSubmitting || !dirty}
                  >
                    {t('Save')}
                  </RaisedButton>
                </div>
              </PageActions>
            )}
            {this.renderKitchenDialog()}
            <>
              {serviceRequests.length ? (
                <div className="table-container">
                  <div
                    id="service-request-info"
                    className="table-service-request-info hidden-scroll"
                    ref={this.serviceRequestInfoVerticalScroll}
                    onScroll={this.synchronizeScroll}
                    onMouseEnter={this.onTableMouseEnter}
                  >
                    <table className="table service-request-info">
                      <thead className="sticky-header">
                        <tr className="service-request-info--header">
                          <th className="request"></th>
                          <th className="service"></th>
                          <th className="location"></th>
                          <th className="kitchen"></th>
                          {this.canRenderNation() && (
                            <th className="nation"></th>
                          )}
                          <th className="unit"></th>
                          {this.canSplitMealPlanner() && (
                            <th className="split"></th>
                          )}
                        </tr>
                        <tr className="service-request-info--header">
                          <th className="request">{t('Request')}</th>
                          <th className="service">{t('Service name')}</th>
                          <th className="location">{t('Location')}</th>
                          <th className="kitchen">{t('Kitchen')}</th>
                          {this.canRenderNation() && (
                            <th className="nation">{t('Nation')}</th>
                          )}
                          <th className="unit">{t('Unit')}</th>
                          {this.canSplitMealPlanner() && (
                            <th className="split">{t('Split')}</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {values.serviceRequests.map((serviceRequest, index) => {
                          return this.renderServiceRequestInfo(
                            serviceRequest,
                            index
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div
                    id="service-request-data"
                    className="table-scroll vertical-scroll"
                    ref={this.serviceRequestDataVerticalScroll}
                    onScroll={this.synchronizeScroll}
                    onMouseEnter={this.onTableMouseEnter}
                  >
                    <table className="table service-request-data">
                      <thead className="sticky-header">
                        <tr>{this.getMonthColumnHeaders()}</tr>
                        <tr>{this.getDateColumnHeaders()}</tr>
                      </thead>
                      <tbody>
                        {values.serviceRequests.map((serviceRequest, index) => {
                          return this.renderServiceRequest(
                            serviceRequest,
                            index,
                            setFieldValue
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="meal-planner--no-services-message">
                  {t('There are no meal services')}
                </div>
              )}
            </>
          </Form>
        )}
      </Formik>
    );
  }

  render() {
    return (
      <div className="meal-planner" ref={this.container}>
        {this.renderHeader()}
        {this.renderFilters()}
        {this.renderServiceRequests()}
      </div>
    );
  }
}

MealPlanner.propTypes = {
  t: PropTypes.func,
  hasUserPermission: PropTypes.func,
  match: PropTypes.object,
  clearMealPlanner: PropTypes.func,
  getServiceRequests: PropTypes.func,
  serviceRequests: PropTypes.array,
  mealPlannerLocations: PropTypes.array,
  queryLocationsForMealPlanner: PropTypes.func,
  getMealPlannerForSubmit: PropTypes.func,
  forSubmit: PropTypes.object,
  updateServiceRequests: PropTypes.func,
  getServiceRequestKitchenForSubmit: PropTypes.func,
  updateServiceRequestKitchen: PropTypes.func,
  kitchenForSubmit: PropTypes.array,
  exportMealPlans: PropTypes.func
};

export default MealPlanner;
